import { useMemo, useState } from "react";
import { endOfDay, startOfDay } from "date-fns";
import {
  Banknote,
  ChevronDown,
  ChevronRight,
  Clock,
  XCircle,
} from "lucide-react";
import { useGetOrdersQuery } from "@/redux/services/order/order.api";
import { Order } from "@/redux/services/order/order.type";
import { cn, displayPrice, formatDate, getOrderStatusText } from "@/lib/utils";
import OrderDetailsDrawer from "@/components/order/OrderDetailsDrawer";
import { reportCard } from "./reportCard";

interface AnomaliesSectionProps {
  restaurantId: string;
  startDate: string;
  endDate: string;
  currency: string;
}

function AnomaliesSection({
  restaurantId,
  startDate,
  endDate,
  currency,
}: AnomaliesSectionProps) {
  const after = startOfDay(new Date(startDate)).toISOString();
  const before = endOfDay(new Date(endDate)).toISOString();

  const baseQuery = {
    "createdAt[after]": after,
    "createdAt[before]": before,
    limit: 100,
  };

  const { data: failed, isLoading: failedLoading } = useGetOrdersQuery({
    restaurantId,
    // Orders absorbed by a merge are canceled too, but they are not lost
    // sales: their lines live on the surviving order (SFAPI-289).
    query: { ...baseQuery, status: "canceled,rejected", merged: false },
  });
  const { data: pending, isLoading: pendingLoading } = useGetOrdersQuery({
    restaurantId,
    query: { ...baseQuery, status: "pending" },
  });
  const { data: unpaid, isLoading: unpaidLoading } = useGetOrdersQuery({
    restaurantId,
    query: { ...baseQuery, isPaid: false },
  });

  const failedOrders = useMemo(() => {
    const list = failed?.results ?? [];
    return list
      .slice()
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
  }, [failed]);

  const pendingOrders = useMemo(() => {
    const list = pending?.results ?? [];
    return list
      .slice()
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
  }, [pending]);

  const openUnpaid = useMemo(() => {
    const list = unpaid?.results ?? [];
    return list.filter((o) => o.status !== "canceled" && o.status !== "rejected" && o.status !== "draft");
  }, [unpaid]);

  // True lost revenue is the *uncollected* part of canceled/rejected orders.
  // Money already collected on them is NOT lost — it must be refunded or
  // reconciled — so it is reported separately instead of being counted as loss.
  // Using remainingAmount here matches the per-order line below, which also
  // shows remainingAmount (SFRESTO-106).
  const failedLost = failedOrders.reduce(
    (sum, o) => sum + (o.remainingAmount ?? o.totalPrice),
    0
  );
  const failedCollected = failedOrders.reduce(
    (sum, o) =>
      sum + Math.max(0, o.totalPrice - (o.remainingAmount ?? o.totalPrice)),
    0
  );
  const pendingRevenue = pendingOrders.reduce(
    (sum, o) => sum + o.totalPrice,
    0
  );
  const unpaidAmount = openUnpaid.reduce(
    (sum, o) => sum + (o.remainingAmount ?? o.totalPrice),
    0
  );

  return (
    <section className="space-y-4">
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Anomalies de la période
      </h2>

      <div className="grid gap-4 lg:grid-cols-3">
        <AnomalyCard
          icon={<Clock className="h-5 w-5 text-red-600" />}
          title="Non acceptées"
          count={pendingOrders.length}
          subtitle={`${displayPrice(pendingRevenue, currency)} jamais traitées`}
          orders={pendingOrders}
          loading={pendingLoading}
          currency={currency}
          emptyText="Toutes les commandes ont été acceptées."
        />
        <AnomalyCard
          icon={<XCircle className="h-5 w-5 text-red-600" />}
          title="Annulées / refusées"
          count={failedOrders.length}
          subtitle={`${displayPrice(failedLost, currency)} de revenu perdu`}
          note={
            failedCollected > 0
              ? `${displayPrice(failedCollected, currency)} encaissés — à rembourser ou rapprocher`
              : undefined
          }
          orders={failedOrders}
          loading={failedLoading}
          currency={currency}
          emptyText="Aucune commande annulée ou refusée."
        />
        <AnomalyCard
          icon={<Banknote className="h-5 w-5 text-amber-600" />}
          title="Impayées encore ouvertes"
          count={openUnpaid.length}
          subtitle={`${displayPrice(unpaidAmount, currency)} en attente`}
          orders={openUnpaid}
          loading={unpaidLoading}
          currency={currency}
          emptyText="Toutes les commandes sont réglées."
        />
      </div>
    </section>
  );
}

interface AnomalyCardProps {
  icon: React.ReactNode;
  title: string;
  count: number;
  subtitle: string;
  note?: string;
  orders: Order[];
  loading: boolean;
  currency: string;
  emptyText: string;
}

function AnomalyCard({
  icon,
  title,
  count,
  subtitle,
  note,
  orders,
  loading,
  currency,
  emptyText,
}: AnomalyCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const canExpand = !loading && orders.length > 0;

  return (
    <>
      <div className={cn(reportCard, "flex flex-col overflow-hidden")}>
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted shrink-0">
              {icon}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground">{title}</p>
              <p className="text-2xl font-semibold tabular-nums text-card-foreground">
                {loading ? "—" : count}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {loading ? "Chargement…" : count === 0 ? emptyText : subtitle}
              </p>
              {!loading && count > 0 && note && (
                <p className="text-xs text-amber-600 mt-1">{note}</p>
              )}
            </div>
          </div>
        </div>

        {canExpand && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center justify-between px-5 py-3 border-t border-border text-sm font-medium text-primary hover:bg-muted/50 transition-colors"
          >
            <span>
              {expanded
                ? "Masquer"
                : `Voir ${count > 1 ? "les commandes" : "la commande"}`}
            </span>
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        )}

        {expanded && canExpand && (
          <ul className="border-t border-border divide-y divide-border">
            {orders.map((order) => (
              <li key={order.id}>
                <button
                  type="button"
                  onClick={() => setSelectedOrder(order)}
                  className="w-full px-5 py-3 text-left text-sm hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="font-medium text-card-foreground truncate">
                        #{order.orderNumber}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(new Date(order.createdAt), true, true)} ·{" "}
                        {getOrderStatusText(order.status)}
                      </span>
                    </div>
                    <span className="tabular-nums font-medium text-card-foreground shrink-0">
                      {displayPrice(
                        order.remainingAmount ?? order.totalPrice,
                        currency
                      )}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedOrder && (
        <OrderDetailsDrawer
          orderId={selectedOrder.id}
          orderNumber={selectedOrder.orderNumber}
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </>
  );
}

export default AnomaliesSection;
